import { useCallback } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useMagicPenStore } from '@src/stores';

import { generateUniqueId } from '../../helpers';

// Constants imports
import { DEFAULT_STROKE_WIDTH } from '../../magicPen.constants';

import { EMagicPenShape, ICanvasObject, ICreatedBy } from '@src/types';

export const useLineTool = (createdBy: ICreatedBy, handleAddObjects: (objects: ICanvasObject[]) => Promise<void>) => {
  const { lineStartPoint, setCurrentLinePoints, setLineStartPoint, brushColor } = useMagicPenStore(
    useShallow((state) => ({
      lineStartPoint: state.lineStartPoint,
      setCurrentLinePoints: state.setCurrentLinePoints,
      setLineStartPoint: state.setLineStartPoint,
      brushColor: state.brushColor,
    }))
  );

  const handleLineMouseMove = useCallback(
    (pos: { x: number; y: number }) => {
      if (lineStartPoint) {
        setCurrentLinePoints([lineStartPoint.x, lineStartPoint.y, pos.x, pos.y]);
      }
    },
    [lineStartPoint, setCurrentLinePoints]
  );

  const handleLineClick = useCallback(
    async (pos: { x: number; y: number }) => {
      if (!lineStartPoint) {
        setLineStartPoint({ x: pos.x, y: pos.y });
        setCurrentLinePoints([pos.x, pos.y]);
      } else {
        const newLine: ICanvasObject = {
          id: generateUniqueId(),
          type: EMagicPenShape.LINE,
          points: [lineStartPoint.x, lineStartPoint.y, pos.x, pos.y],
          stroke: brushColor,
          strokeWidth: DEFAULT_STROKE_WIDTH,
          createdBy,
        };
        await handleAddObjects([newLine]);
        setLineStartPoint(null);
        setCurrentLinePoints([]);
      }
    },
    [lineStartPoint, setLineStartPoint, setCurrentLinePoints, brushColor, createdBy, handleAddObjects]
  );

  return {
    handleLineMouseMove,
    handleLineClick,
  };
};

export default useLineTool;
